<script setup>
const DEFAULT_ICON_URL = "/images/site/logo-top.svg";

const props = defineProps({
  firstTeam: {
    type: Object,
    required: true,
  },
  secondTeam: {
    type: Object,
    required: true,
  },
  showCity: {
    type: Boolean,
    default: true,
  },
  isHomeLogoWithOpponent: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: ''
  },
  className: {
    type: String,
    default: '',
  },
  isSmall: {
    type: Boolean,
    default: false,
  },
});

const imgSize = props.isSmall ? 32 : 50;
</script>

<template>
  <div class="i-teams" :class="[
    className,
    {
      'i-teams_small': isSmall
    }
  ]">
    <template v-if="isHomeLogoWithOpponent">
      <div class="i-teams__wrapper">
        <div class="i-teams__item">
          <div class="i-teams__desc">
            <div class="i-teams__name">{{ firstTeam.name }}</div>
            <div v-if="showCity" class="i-teams__city">{{ firstTeam.city }}</div>
          </div>
          <div class="i-teams__image">
            <img :width="imgSize" :height="imgSize" :src="firstTeam.imageUrl || DEFAULT_ICON_URL"
              :alt="firstTeam.name + ' - Лого'" />
          </div>
        </div>

        <div class="i-teams__info">
          <strong class="i-teams__game">:</strong>
        </div>

        <div class="i-teams__item">
          <div class="i-teams__desc">
            <div class="i-teams__name">{{ secondTeam?.name }}</div>
            <div v-if="showCity" class="i-teams__city">{{ secondTeam?.city }}</div>
          </div>
          <div class="i-teams__image">
            <img :width="imgSize" :height="imgSize" :src="secondTeam?.imageUrl || DEFAULT_ICON_URL"
              :alt="secondTeam?.name + ' - Лого'" />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="i-teams__wrapper i-teams__wrapper_single">
        <div class="i-teams__item i-teams__item_single">
          <div class="i-teams__desc">
            <p class="i-teams__name">{{ name || firstTeam?.name }}</p>
            <p v-if="showCity" class="i-teams__city">{{ firstTeam?.city }}</p>
          </div>
          <div class="i-teams__image" :class="{ 'i-teams__image_small': isSmall }">
            <img :width="imgSize" :height="imgSize" :src="firstTeam?.imageUrl || DEFAULT_ICON_URL"
              :alt="firstTeam?.name + ' - Лого'">
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";

.i-teams {
  &_order {
    .i-teams {
      &__wrapper {
        justify-content: flex-start;

        &:has(> :only-child) {
          margin-left: 0;
        }
      }

      &__desc {
        width: auto;
      }

      &__info {
        margin: auto 16px;
      }

      &__name {
        font-family: 'Montserrat', sans-serif;
      }
    }
  }

  &_order-package {
    .i-teams {
      &__desc {
        width: 220px;
      }

      &__city {
        font-size: 14px;
      }

      &__info {
        margin: 0 20px;
      }

      &__image {
        width: 50px;
        height: 50px;
        min-width: 50px;
        min-height: 50px;
      }

      &__wrapper {
        &:has(> :only-child) {
          margin-left: 236px;
        }
      }

      &__item {
        &_single {
          margin: 0;
        }
      }

      &__name {
        font-family: 'Montserrat', sans-serif;
      }
    }
  }

  &_main {
    position: relative;

    .i-teams {
      &__item {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        min-width: initial;

        &:first-child {
          right: 100%;
          text-align: right;
        }

        &:last-child {
          left: 100%;
        }
      }

      &__name {
        white-space: nowrap;
      }

      &__info {
        margin-top: 0;
      }
    }
  }

  &_page {
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;

    .i-teams {
      &__wrapper {
        justify-content: center;
      }

      &__item {
        border: 1px solid $border-color;
        padding: 30px;
        flex-direction: column-reverse;
        text-align: center;
        min-width: 200px;
        border-radius: $border-radius;

        &:last-child {
          flex-direction: column-reverse;

          .i-teams {
            &__image {
              margin-right: 0;
            }
          }
        }
      }

      &__game {
        font-size: 20px;
        display: block;
        margin-top: 75px;
      }

      &__info {
        .i-teams__time~.i-teams__game {
          margin-top: 0;
        }
      }

      &__image {
        width: 100px;
        height: 100px;
        margin-bottom: 20px;
      }

      &__desc {
        margin-right: 0;
      }
    }
  }

  &_subscription {
    .i-teams {
      &__link {
        display: block;
        pointer-events: none;
      }
    }
  }

  &_verification {
    margin-bottom: 0;

    .i-teams {
      &__wrapper {
        justify-content: flex-start;
        margin-left: 0;

        &:has(> :only-child) {
          margin-left: 0;
        }
      }

      &__item {
        min-width: initial;

        &:last-child {
          .i-teams__image {
            margin-right: 24px;
          }
        }
      }

      &__desc {
        width: auto;
        margin-right: 24px;
      }

      &__image {
        width: 40px;
        min-width: 40px;
        height: 40px;
        min-height: 40px;
      }

      &__info {
        margin: 0 16px;
      }
    }
  }

  &_small {
    .i-teams {
      &__name {
        font-size: 14px;
      }

      &__item {
        &:last-child {
          .i-teams {
            &__image {
              margin-right: 12px;
            }
          }
        }
      }

      &__image {
        width: 32px;
        height: 32px;
        min-width: 32px;
        min-height: 32px;
      }

      &__desc {
        margin-right: 14px;
      }
    }
  }

  &__wrapper {
    display: flex;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    max-width: initial;
    min-width: initial;

    &_single {
      margin: 0 auto;
      width: auto;

      .i-teams__desc {
        width: 100%;
      }
    }

    &:last-child {
      flex-direction: row-reverse;

      .i-teams {
        &__desc {
          margin-right: 0;
          text-align: left;
        }

        &__image {
          margin-right: 16px;
        }
      }
    }
  }

  &__desc {
    margin-right: 16px;
    text-align: right;
    width: 200px;
  }

  &__name {
    transition: color 0.3s;
    font-family: 'Oswald', sans-serif;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    line-height: 130%;
  }

  &__city {
    font-size: 14px;
    font-weight: 500;
    color: $gray-medium;
    margin-top: 4px;
  }

  &__image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 50px;
    height: 50px;
    min-width: 50px;
    min-height: 50px;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
    }
  }

  &__image_small {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    margin-right: 12px;
  }

  &__info {
    margin: 0 20px;
    display: flex;
    align-items: center;
    font-size: 20px;
    font-family: 'Open Sans', sans-serif;
  }

  &__info_small {
    align-self: center;
    margin: 0 15px;
  }

  &__game {
    white-space: nowrap;
    font-family: "Montserrat", Arial, sans-serif;
  }

  &__date,
  &__time {
    font-weight: 700;
  }

  &__date {
    margin-bottom: 10px;
    font-size: 24px;
  }

  &__time {
    margin-bottom: 25px;
  }

  @media (max-width: $screen-lg) {
    &__desc {
      width: 150px;
    }
  }

  @media screen and (max-width: $screen-md) {

    &_main,
    &_subscription {
      .i-teams {
        &__wrapper {
          justify-content: space-between;
        }

        &__item {
          max-width: initial;
          width: 40%;
          position: static;
          transform: initial;
        }

        &__info {
          margin-top: 12px;
        }
      }
    }

    &__desc {
      width: 150px;
    }
  }

  @media (max-width: $screen-md-min) {
    &__desc {
      text-align: center;
      width: auto;
      min-width: 100px;
    }

    &__image {
      margin-bottom: 8px;
    }

    &__item {
      flex-direction: column-reverse;
      text-align: center;

      &:first-child {
        .i-teams {
          &__desc {
            margin-right: 0;
          }
        }
      }

      &_single {
        max-width: initial;
      }

      &:last-child {
        flex-direction: column-reverse;

        .i-teams {
          &__image {
            margin-right: 0;
          }

          &__desc {
            text-align: center;
          }
        }
      }

      &_single {
        width: auto;

        &:last-child {
          .i-teams {
            &__image {
              margin-bottom: 8px;
            }
          }
        }

        .i-teams {
          &__image {
            min-width: 56px;
            min-height: 56px;
            width: 56px;
            height: 56px;
          }
        }
      }
    }

    &__info {
      margin: 0 12px;
    }

    &__wrapper {
      display: grid;
      grid-template-columns: 1fr auto 1fr;

      &:has(> :only-child) {
        margin-left: 0;
      }

      justify-content: center;

      &_single {
        grid-template-columns: 1fr;
      }
    }

    &_order {
      width: 100%;

      .i-teams {
        &__wrapper {
          justify-content: center;

          &:has(> :only-child) {
            width: 100%;
          }
        }

        &__item {
          &:last-child {
            .i-teams {
              &__image {
                margin-right: 0;
              }
            }
          }

          &_single {
            &:last-child {
              .i-teams {
                &__image {
                  margin-right: 24px;
                }
              }
            }
          }
        }

        &__info {
          margin: auto 24px;
        }
      }
    }

    &_subscription {
      width: 100%;

      .i-teams {
        &__item {
          &:first-child {
            text-align: center;
          }
        }
      }
    }

    &_verification {
      width: 100%;

      .i-teams {
        &__wrapper {
          justify-content: center;
        }

        &__info {
          margin: 0 30px;
          margin-top: 12px;
        }

        &__image {
          width: 48px;
          min-width: 48px;
          height: 48px;
          min-height: 48px;
        }

        &__item {
          &:last-child {
            .i-teams {
              &__image {
                margin-right: 0;
              }
            }
          }

          &:only-child {
            .i-teams {
              &__image {
                margin-right: 24px;
              }
            }
          }
        }
      }
    }

    &__name {
      white-space: initial;
    }

    &_main {
      width: 100%;

      .i-teams {
        &__name {
          white-space: initial;
        }

        &__item {
          &:first-child {
            text-align: center;
          }
        }
      }
    }

    &_order-package {
      .i-teams {
        &__wrapper {
          &:has(> :only-child) {
            margin-left: 0;
          }
        }
      }
    }
  }

  @media (max-width: $screen-xs) {
    &__item {
      min-width: 100px;
      flex-grow: 1;
    }

    &_small {
      margin-bottom: 0;

      .i-teams {
        &__wrapper {
          align-items: center;
        }

        &__image {
          width: 50px;
          min-width: 50px;
          height: 50px;
          min-height: 50px;
          margin-right: 0;
        }

        &__item {
          max-width: initial;
          min-width: initial;
          justify-content: center;

          &:only-child {
            width: 100%;
          }
        }

        &__info {
          margin: 0 10px;
          min-width: initial;
        }

        &__name {
          font-size: 18px;
        }
      }
    }
  }
}

.i-event-item {
  &__package-item {
    .i-teams__wrapper:has(> :only-child) {
      margin-left: 216px;
    }
  }

  @media (max-width: $screen-md-min) {
    &__package-item {
      .i-teams__wrapper:has(> :only-child) {
        margin-left: 0;
      }
    }
  }
}
</style>
